/* RESPONSIBLE TEAM: team-standalone */
import Platform from './platform';
import type IntercomMessengerConfiguration from 'embercom/models/standalone/intercom-messenger-configuration';
import type ApplicationInstance from '@ember/application/instance';
import { IntegrationState, IntegrationType, StandalonePlatform } from '../constants';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { CustomHelpdeskConversationExtractionRowData } from './component-data/conversation-extraction-row-data';
import GenericConversationExcerpt from './component-data/generic/conversation-excerpt';
import GenericOnboardingGuideStep from './component-data/generic/onboarding-guide-step';
import GenericRequestAccessData from './component-data/generic/request-access';
import GenericChannelTargetingBodyData from './component-data/generic/channel-targeting-body-data';
import { GenericAiContentLibraryData } from './component-data/ai-content-library';
import { GenericKnowledgeContentData } from './component-data/knowlege-content-data';
import { GenericComposerOverflowMenuTooltipData } from './component-data/overflow-menu-tooltip-data';
import { GenericExampleCodeSnippetData } from './component-data/example-code-snippet-data';

export default class CustomHelpdeskPlatform extends Platform {
  @service declare finStandaloneService: FinStandaloneService;

  intercomMessengerConfig?: IntercomMessengerConfiguration;

  constructor(
    owner: ApplicationInstance,
    inputs: { intercomMessengerConfig?: IntercomMessengerConfiguration },
  ) {
    super(owner);
    this.intercomMessengerConfig = inputs.intercomMessengerConfig;
  }

  private get customHelpdeskIntegration() {
    return this.finStandaloneService.standaloneIntegrations.find(
      (integration) => integration.integrationType === IntegrationType.CustomHelpdesk,
    );
  }

  get componentData(): Platform['componentData'] {
    return {
      'conversation-extraction': CustomHelpdeskConversationExtractionRowData,
      'conversation-excerpt': new GenericConversationExcerpt(this),
      'knowledge-hub-modal': null,
      'onboarding-guide-step': new GenericOnboardingGuideStep(this),
      'request-access': new GenericRequestAccessData(this),
      'channel-targeting-body': new GenericChannelTargetingBodyData(),
      'ai-content-library': GenericAiContentLibraryData,
      'knowledge-content': GenericKnowledgeContentData,
      'composer-overflow-menu-tooltip': GenericComposerOverflowMenuTooltipData,
      'example-code-snippet': GenericExampleCodeSnippetData,
      'conversation-ingestion': null,
    } as const;
  }

  async loadConfigData() {
    // TODO
  }

  get supportsErrorLogs() {
    return true;
  }

  get showOauthBanner() {
    return false;
  }

  async loadRequiredData() {
    // TODO
  }

  async loadDataFields() {
    // TODO
  }

  get isConnected() {
    return false;
  }

  get hasInstalledCopilot() {
    return false;
  }

  get supportsHistoricalSyncing() {
    return false;
  }

  get historicalSyncingEnabled() {
    return false;
  }

  get finIsLive() {
    return (
      this.customHelpdeskIntegration?.integrationState === IntegrationState.Enabled ||
      this.intercomMessengerConfig?.isMessengerEnabled ||
      false
    );
  }

  get finIsPaused() {
    return this.customHelpdeskIntegration?.integrationState === IntegrationState.Paused;
  }

  get connectionState() {
    return this.customHelpdeskIntegration?.integrationState ?? IntegrationState.NotConfigured;
  }

  get icon() {
    return 'circle-filled' as const;
  }

  get platformKey() {
    return StandalonePlatform.CustomHelpdesk;
  }

  get supportsFinPlayground() {
    return false;
  }

  get supportsConversationSyncing() {
    return false;
  }

  findMappedConversationField(_id: string | undefined) {
    return undefined;
  }

  isMappedUserAttribute(_attributeIdentifier: string) {
    return false;
  }

  isMappedCompanyAttribute(_attributeIdentifier: string) {
    return false;
  }

  isMappedConversationAttribute(_attributeIdentifier: string) {
    return false;
  }

  get ticketFields() {
    return [];
  }

  get userFields() {
    return [];
  }

  get companyFields() {
    return [];
  }
}
